import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Field,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Skeleton,
  SkeletonItem,
  SpinButton,
  Text,
} from '@fluentui/react-components';
import type { SpinButtonChangeEvent, SpinButtonOnChangeData } from '@fluentui/react-components';
import { PermissionBanner } from '@/components/shared/PermissionBanner';
import { useScaleConfiguration, useUpdateScaleConfiguration } from '@/hooks/useApplicationManagement';
import { useAppPermission } from '@/hooks/useAppPermission';
import { useTelemetry } from '@/hooks/useTelemetry';
import type { ScaleConfiguration } from '@/types/automation';
import { useScaleConfigurationTabStyles } from './ScaleConfigurationTab.styles';

export interface ScaleConfigurationTabProps {
  projectName: string;
  appName: string;
}

interface ScaleValidationErrors {
  minReplicas?: string;
  maxReplicas?: string;
  cooldownPeriod?: string;
  pollingInterval?: string;
}

const SUCCESS_MESSAGE_TIMEOUT_MS = 3000;

function parseIntegerInput(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed)) {
    return null;
  }

  return parsed;
}

function getValidationErrors(
  minReplicasInput: string,
  maxReplicasInput: string,
  cooldownPeriodInput: string,
  pollingIntervalInput: string,
): ScaleValidationErrors {
  const errors: ScaleValidationErrors = {};
  const minReplicas = parseIntegerInput(minReplicasInput);
  const maxReplicas = parseIntegerInput(maxReplicasInput);
  const cooldownPeriod = parseIntegerInput(cooldownPeriodInput);
  const pollingInterval = parseIntegerInput(pollingIntervalInput);

  if (minReplicas === null || minReplicas < 0) {
    errors.minReplicas = minReplicas === null
      ? 'Min replicas must be a valid integer.'
      : 'Min replicas must be greater than or equal to 0.';
  }

  if (maxReplicas === null || maxReplicas < 1) {
    errors.maxReplicas = maxReplicas === null
      ? 'Max replicas must be a valid integer.'
      : 'Max replicas must be greater than or equal to 1.';
  }

  if (
    minReplicas !== null
    && maxReplicas !== null
    && minReplicas >= 0
    && maxReplicas >= 1
    && minReplicas > maxReplicas
  ) {
    errors.minReplicas = 'Min replicas must be less than or equal to max replicas.';
    errors.maxReplicas = 'Max replicas must be greater than or equal to min replicas.';
  }

  if (cooldownPeriod === null || cooldownPeriod < 0) {
    errors.cooldownPeriod = cooldownPeriod === null
      ? 'Cooldown period must be a valid integer.'
      : 'Cooldown period must be greater than or equal to 0.';
  }

  if (pollingInterval === null || pollingInterval < 1) {
    errors.pollingInterval = pollingInterval === null
      ? 'Polling interval must be a valid integer.'
      : 'Polling interval must be greater than or equal to 1.';
  }

  return errors;
}

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const record = error as Record<string, unknown>;
    const code = typeof record.code === 'string' ? record.code : undefined;
    const message = typeof record.message === 'string' ? record.message : undefined;

    if (code === 'InvalidScaleConfiguration' && message) {
      return message;
    }

    if (message) {
      return message;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'An unexpected error occurred while saving the scale configuration.';
}

export const ScaleConfigurationTab = ({ projectName, appName }: ScaleConfigurationTabProps) => {
  const styles = useScaleConfigurationTabStyles();
  const { trackEvent, trackException } = useTelemetry();
  const { canEdit, isLoading: isPermissionLoading } = useAppPermission();
  const { data, isLoading, isError, error } = useScaleConfiguration(projectName, appName);
  const updateMutation = useUpdateScaleConfiguration(projectName, appName);

  const [minReplicasInput, setMinReplicasInput] = useState('');
  const [maxReplicasInput, setMaxReplicasInput] = useState('');
  const [cooldownPeriodInput, setCooldownPeriodInput] = useState('');
  const [pollingIntervalInput, setPollingIntervalInput] = useState('');
  const [baselineConfiguration, setBaselineConfiguration] = useState<ScaleConfiguration | null>(null);
  const [saveSuccessVisible, setSaveSuccessVisible] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applyInputs = useCallback((configuration: ScaleConfiguration) => {
    setMinReplicasInput(String(configuration.minReplicas));
    setMaxReplicasInput(String(configuration.maxReplicas));
    setCooldownPeriodInput(String(configuration.cooldownPeriod));
    setPollingIntervalInput(String(configuration.pollingInterval));
  }, []);

  useEffect(() => {
    trackEvent('settings.scale.viewed');
  }, [trackEvent]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const validationErrors = useMemo(
    () => getValidationErrors(minReplicasInput, maxReplicasInput, cooldownPeriodInput, pollingIntervalInput),
    [minReplicasInput, maxReplicasInput, cooldownPeriodInput, pollingIntervalInput],
  );

  const isDirty = useMemo(() => {
    if (!baselineConfiguration) {
      return false;
    }

    return (
      parseIntegerInput(minReplicasInput) !== baselineConfiguration.minReplicas
      || parseIntegerInput(maxReplicasInput) !== baselineConfiguration.maxReplicas
      || parseIntegerInput(cooldownPeriodInput) !== baselineConfiguration.cooldownPeriod
      || parseIntegerInput(pollingIntervalInput) !== baselineConfiguration.pollingInterval
    );
  }, [baselineConfiguration, minReplicasInput, maxReplicasInput, cooldownPeriodInput, pollingIntervalInput]);

  useEffect(() => {
    if (data && (!baselineConfiguration || !isDirty)) {
      setBaselineConfiguration(data);
      applyInputs(data);
    }
  }, [applyInputs, baselineConfiguration, data, isDirty]);

  useEffect(() => {
    if (isDirty && saveSuccessVisible) {
      setSaveSuccessVisible(false);
    }
  }, [isDirty, saveSuccessVisible]);

  const hasValidationErrors = Boolean(
    validationErrors.minReplicas
    || validationErrors.maxReplicas
    || validationErrors.cooldownPeriod
    || validationErrors.pollingInterval,
  );
  const minReplicas = parseIntegerInput(minReplicasInput);
  const maxReplicas = parseIntegerInput(maxReplicasInput);
  const cooldownPeriod = parseIntegerInput(cooldownPeriodInput);
  const pollingInterval = parseIntegerInput(pollingIntervalInput);
  const minValidationMessage = validationErrors.minReplicas
    ? <Text className={styles.validationError}>{validationErrors.minReplicas}</Text>
    : null;
  const maxValidationMessage = validationErrors.maxReplicas
    ? <Text className={styles.validationError}>{validationErrors.maxReplicas}</Text>
    : null;
  const cooldownValidationMessage = validationErrors.cooldownPeriod
    ? <Text className={styles.validationError}>{validationErrors.cooldownPeriod}</Text>
    : null;
  const pollingValidationMessage = validationErrors.pollingInterval
    ? <Text className={styles.validationError}>{validationErrors.pollingInterval}</Text>
    : null;
  const currentConfiguration = baselineConfiguration ?? data ?? null;

  const handleMinReplicasChange = useCallback((_event: SpinButtonChangeEvent, data: SpinButtonOnChangeData) => {
    setMinReplicasInput(data.value !== undefined && data.value !== null ? String(data.value) : (data.displayValue ?? ''));
  }, []);

  const handleMaxReplicasChange = useCallback((_event: SpinButtonChangeEvent, data: SpinButtonOnChangeData) => {
    setMaxReplicasInput(data.value !== undefined && data.value !== null ? String(data.value) : (data.displayValue ?? ''));
  }, []);

  const handleCooldownPeriodChange = useCallback((_event: SpinButtonChangeEvent, data: SpinButtonOnChangeData) => {
    setCooldownPeriodInput(data.value !== undefined && data.value !== null ? String(data.value) : (data.displayValue ?? ''));
  }, []);

  const handlePollingIntervalChange = useCallback((_event: SpinButtonChangeEvent, data: SpinButtonOnChangeData) => {
    setPollingIntervalInput(data.value !== undefined && data.value !== null ? String(data.value) : (data.displayValue ?? ''));
  }, []);

  const handleDiscard = useCallback(() => {
    if (!baselineConfiguration) {
      return;
    }

    applyInputs(baselineConfiguration);
    setSaveErrorMessage(null);
  }, [applyInputs, baselineConfiguration]);

  const handleSave = useCallback(async () => {
    if (
      !baselineConfiguration
      || minReplicas === null
      || maxReplicas === null
      || cooldownPeriod === null
      || pollingInterval === null
      || hasValidationErrors
    ) {
      return;
    }

    setSaveErrorMessage(null);
    setSaveSuccessVisible(false);

    try {
      const updatedConfiguration = await updateMutation.mutateAsync({
        minReplicas,
        maxReplicas,
        cooldownPeriod,
        pollingInterval,
      });

      setBaselineConfiguration(updatedConfiguration);
      applyInputs(updatedConfiguration);
      trackEvent('settings.scale.updated', undefined, {
        min_replicas: updatedConfiguration.minReplicas,
        max_replicas: updatedConfiguration.maxReplicas,
        cooldown_period: updatedConfiguration.cooldownPeriod,
        polling_interval: updatedConfiguration.pollingInterval,
      });
      setSaveSuccessVisible(true);

      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }

      successTimerRef.current = setTimeout(() => {
        setSaveSuccessVisible(false);
      }, SUCCESS_MESSAGE_TIMEOUT_MS);
    } catch (saveError) {
      setSaveErrorMessage(getErrorMessage(saveError));
      trackException(saveError, { op: 'settings.scale.update' });
    }
  }, [applyInputs, baselineConfiguration, cooldownPeriod, hasValidationErrors, maxReplicas, minReplicas, pollingInterval, trackEvent, trackException, updateMutation]);

  if (isLoading || isPermissionLoading || (!baselineConfiguration && data)) {
    return (
      <div className={styles.loadingState} role="status" aria-label="Loading scale configuration">
        <div className={styles.skeletonCard}>
          <Skeleton>
            <div className={styles.skeletonFieldRow}>
              <SkeletonItem className={styles.skeletonField} />
              <SkeletonItem className={styles.skeletonField} />
            </div>
            <div className={styles.skeletonFieldRow}>
              <SkeletonItem className={styles.skeletonInput} />
            </div>
          </Skeleton>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.errorState}>
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Failed to load scale configuration</MessageBarTitle>
            {getErrorMessage(error)}
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  if (!currentConfiguration) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <Text as="h2" className={styles.title}>Scale configuration</Text>
          {isDirty && <Text className={styles.dirtyIndicator}>Unsaved changes</Text>}
        </div>
        <Text className={styles.description}>
          Configure the replica limits and autoscale timing for this application. Cooldown period and
          polling interval are specified in seconds.
        </Text>
      </div>

      {!canEdit && (
        <PermissionBanner
          title="Read-only access"
          message="You can view the current scale configuration, but you need Contributor access to change replica limits."
        />
      )}

      <div role="status" aria-live="polite">
        {saveErrorMessage && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Failed to save scale configuration</MessageBarTitle>
              {saveErrorMessage}
            </MessageBarBody>
          </MessageBar>
        )}

        {saveSuccessVisible && (
          <MessageBar intent="success" className={styles.successBar}>
            <MessageBarBody>
              <MessageBarTitle>Scale configuration saved</MessageBarTitle>
              Replica limits were updated successfully.
            </MessageBarBody>
          </MessageBar>
        )}
      </div>

      <div className={styles.formCard}>
        <div className={styles.formFields}>
          <div className={styles.fieldRow}>
            {canEdit ? (
              <>
                <Field
                  className={styles.field}
                  label="Minimum replicas"
                  validationState={validationErrors.minReplicas ? 'error' : 'none'}
                  validationMessage={minValidationMessage}
                >
                  <SpinButton
                    className={styles.input}
                    value={parseIntegerInput(minReplicasInput) ?? null}
                    displayValue={minReplicasInput}
                    min={0}
                    step={1}
                    onChange={handleMinReplicasChange}
                    aria-label="Minimum replicas"
                  />
                </Field>
                <Field
                  className={styles.field}
                  label="Maximum replicas"
                  validationState={validationErrors.maxReplicas ? 'error' : 'none'}
                  validationMessage={maxValidationMessage}
                >
                  <SpinButton
                    className={styles.input}
                    value={parseIntegerInput(maxReplicasInput) ?? null}
                    displayValue={maxReplicasInput}
                    min={1}
                    step={1}
                    onChange={handleMaxReplicasChange}
                    aria-label="Maximum replicas"
                  />
                </Field>
              </>
            ) : (
              <>
                <Field className={styles.field} label="Minimum replicas">
                  <Text className={styles.readOnlyValue}>{currentConfiguration.minReplicas}</Text>
                </Field>
                <Field className={styles.field} label="Maximum replicas">
                  <Text className={styles.readOnlyValue}>{currentConfiguration.maxReplicas}</Text>
                </Field>
              </>
            )}
          </div>
          <div className={styles.fieldRow}>
            {canEdit ? (
              <>
                <Field
                  className={styles.field}
                  label="Cooldown period (seconds)"
                  validationState={validationErrors.cooldownPeriod ? 'error' : 'none'}
                  validationMessage={cooldownValidationMessage}
                >
                  <SpinButton
                    className={styles.input}
                    value={parseIntegerInput(cooldownPeriodInput) ?? null}
                    displayValue={cooldownPeriodInput}
                    min={0}
                    step={1}
                    onChange={handleCooldownPeriodChange}
                    aria-label="Cooldown period (seconds)"
                  />
                </Field>
                <Field
                  className={styles.field}
                  label="Polling interval (seconds)"
                  validationState={validationErrors.pollingInterval ? 'error' : 'none'}
                  validationMessage={pollingValidationMessage}
                >
                  <SpinButton
                    className={styles.input}
                    value={parseIntegerInput(pollingIntervalInput) ?? null}
                    displayValue={pollingIntervalInput}
                    min={1}
                    step={1}
                    onChange={handlePollingIntervalChange}
                    aria-label="Polling interval (seconds)"
                  />
                </Field>
              </>
            ) : (
              <>
                <Field className={styles.field} label="Cooldown period (seconds)">
                  <Text className={styles.readOnlyValue}>{currentConfiguration.cooldownPeriod}</Text>
                </Field>
                <Field className={styles.field} label="Polling interval (seconds)">
                  <Text className={styles.readOnlyValue}>{currentConfiguration.pollingInterval}</Text>
                </Field>
              </>
            )}
          </div>
        </div>

        {canEdit && isDirty && (
          <div className={styles.actionBar}>
            <Button appearance="subtle" onClick={handleDiscard} disabled={updateMutation.isPending}>
              Discard
            </Button>
            <Button
              appearance="primary"
              onClick={() => {
                void handleSave();
              }}
              disabled={updateMutation.isPending || hasValidationErrors}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
